import { useRef, useState } from 'react';
import { Box, Text, Group, Image, ActionIcon, Stack, Loader } from '@mantine/core';
import { IconPhoto, IconX, IconUpload } from '@tabler/icons-react';
import {
  assetsApi,
  assetUrl,
  ACCEPTED_IMAGE_MIME_TYPES,
  MAX_IMAGE_BYTES,
} from '@/services/assets.api';
import { useImageUpload, dragHasImages, imageFilesFrom } from '@/features/editor/hooks/useImageUpload';
import { NAVY } from './styles';

interface Props {
  element: any;
  label: string;
  /** Asset id, or an array of ids when the element allows multiple. */
  value: string | string[] | undefined;
  onChange: (value: string | string[] | undefined) => void;
  /**
   * Live session id. Absent in preview, where the in-memory session has no DB
   * row and the authenticated upload endpoint is used instead.
   */
  sessionId?: string | null;
  mode?: 'preview' | 'live';
}

/** Map the element's authored format list onto the MIME types the picker offers. */
function acceptedMimeTypes(formats?: string[]): string[] {
  if (!formats || formats.length === 0) return ACCEPTED_IMAGE_MIME_TYPES;
  const byFormat: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
  };
  const mapped = formats
    .map((f) => byFormat[f.toLowerCase().replace(/^\./, '')])
    .filter(Boolean);
  // An authored list naming nothing we support shouldn't silently accept all.
  return Array.from(new Set(mapped));
}

export function ImageUploadElement({
  element,
  label,
  value,
  onChange,
  sessionId,
  mode = 'preview',
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  // Nested dragenter/dragleave from child elements would otherwise flicker the
  // highlight; count them instead of toggling a boolean.
  const dragDepth = useRef(0);

  const multiple = Boolean(element.multiple);
  const accepted = acceptedMimeTypes(element.acceptedFormats);
  const acceptList = accepted.length > 0 ? accepted : ACCEPTED_IMAGE_MIME_TYPES;
  const maxBytes = element.maxSizeMB
    ? Math.min(element.maxSizeMB * 1024 * 1024, MAX_IMAGE_BYTES)
    : MAX_IMAGE_BYTES;

  const { uploading, uploadMany } = useImageUpload({
    maxBytes,
    acceptedMimeTypes: acceptList,
  });

  const ids: string[] = value ? (Array.isArray(value) ? value : [value]) : [];

  const commit = (next: string[]) => {
    if (multiple) onChange(next.length > 0 ? next : undefined);
    else onChange(next[0]);
  };

  const handleFiles = async (incoming: File[]) => {
    if (incoming.length === 0) return;
    // A single-image element takes only the first file, however many arrive.
    const files = multiple ? incoming : incoming.slice(0, 1);

    const assets = await uploadMany(files, (file) =>
      // A preview session lives only in API memory, so it has no session row to
      // authorize against. The author driving the preview is authenticated, so
      // the normal upload endpoint serves.
      mode === 'live' && sessionId
        ? assetsApi.uploadSessionAsset(sessionId, file, {
            maxSizeMB: element.maxSizeMB,
            acceptedFormats: element.acceptedFormats,
          })
        : assetsApi.uploadFlowContent(file),
    );

    if (assets.length === 0) return;
    const uploadedIds = assets.map((a) => a.id);
    commit(multiple ? [...ids, ...uploadedIds] : uploadedIds);
  };

  const canAddMore = multiple || ids.length === 0;

  return (
    <Box>
      <Text size="sm" fw={500} mb="xs" c="#2A3140">
        {label}
      </Text>

      <input
        ref={inputRef}
        type="file"
        accept={acceptList.join(',')}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          // Reset first so re-picking the same file still fires onChange.
          event.target.value = '';
          void handleFiles(files);
        }}
      />

      {ids.length > 0 && (
        <Stack gap="xs" mb="xs">
          {ids.map((id) => (
            <Group key={id} gap="xs" align="center">
              <Image src={assetUrl(id)} alt="Uploaded image" w={80} h={80} fit="cover" radius="md" />
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => commit(ids.filter((existing) => existing !== id))}
                aria-label="Remove image"
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      )}

      {canAddMore && (
        <Box
          role="button"
          tabIndex={0}
          aria-label={multiple ? 'Add images' : 'Add image'}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            // Keyboard parity with the click target: this is a div, so Enter
            // and Space do not activate it for free.
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragEnter={(e) => {
            if (!dragHasImages(e.dataTransfer)) return;
            e.preventDefault();
            dragDepth.current += 1;
            setDragActive(true);
          }}
          onDragOver={(e) => {
            if (!dragHasImages(e.dataTransfer)) return;
            // Without preventDefault the browser navigates to the dropped file.
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDragLeave={() => {
            dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (dragDepth.current === 0) setDragActive(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            dragDepth.current = 0;
            setDragActive(false);
            void handleFiles(imageFilesFrom(e.dataTransfer));
          }}
          style={{
            border: `1px dashed ${dragActive ? NAVY : '#D9DEE8'}`,
            backgroundColor: dragActive ? '#F2F5FB' : '#FFFFFF',
            borderRadius: 8,
            padding: '18px 14px',
            cursor: uploading ? 'progress' : 'pointer',
            transition: 'background-color 120ms ease, border-color 120ms ease',
          }}
        >
          <Group gap="xs" align="center" justify="center">
            {uploading ? <Loader size="xs" /> : dragActive ? <IconUpload size={18} color={NAVY} /> : <IconPhoto size={18} style={{ color: '#8A93A3' }} />}
            <Text size="sm" c={dragActive ? NAVY : 'dimmed'}>
              {uploading
                ? 'Uploading…'
                : dragActive
                  ? `Drop to upload`
                  : `Drag ${multiple ? 'images' : 'an image'} here, or click to browse`}
            </Text>
          </Group>
          {!uploading && !dragActive && (
            <Text size="xs" c="dimmed" ta="center" mt={4}>
              {acceptList.map((m) => m.replace('image/', '').toUpperCase()).join(', ')} · up to{' '}
              {Math.round(maxBytes / (1024 * 1024))}MB
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}
